<script setup lang="ts">
import { useSchool } from '~/composables/useSchool'

const { getSchoolInfo } = useSchool()
const { data: info } = await useAsyncData('school-info', () => getSchoolInfo(), { lazy: true })

const { setPageSeo } = useSeo()

await setPageSeo({
  title: 'Politique de Confidentialité',
  description: 'Politique de confidentialité de l\'école OXO : données collectées et leur usage, protection, cookies et mesure d\'audience, paiement en ligne avec Stripe.'
})
</script>

<template>
  <div class="pt-32 pb-24 bg-slate-50 dark:bg-oxo-dark min-h-screen">
    <div class="container mx-auto px-6 max-w-4xl">
      <h1 class="font-display font-bold text-4xl mb-12 text-slate-900 dark:text-white">Politique de Confidentialité</h1>

      <div class="prose prose-lg dark:prose-invert max-w-none space-y-8">
        
        <p class="lead text-xl text-gray-600 dark:text-gray-300">
            L'École OXO accorde une grande importance à la protection de votre vie privée. 
            Cette politique détaille comment nous collectons, utilisons et protégeons vos données personnelles.
        </p>

        <section aria-labelledby="collecte">
          <h2 id="collecte" class="text-2xl font-bold mb-4 text-oxo-red">1. Collecte des informations</h2>
          <p>
            Nous recueillons des informations lorsque vous utilisez notre formulaire de contact ou de candidature. 
            Les informations recueillies incluent votre nom, votre adresse e-mail,  numéro de téléphone, et éventuellement votre CV/Portfolio.
          </p>
        </section>

        <section aria-labelledby="utilisation">
          <h2 id="utilisation" class="text-2xl font-bold mb-4 text-oxo-red">2. Utilisation des informations</h2>
          <p>
            Toutes les informations que nous recueillons auprès de vous peuvent être utilisées pour :
          </p>
          <ul class="list-disc pl-6">
            <li>Personnaliser votre expérience et répondre à vos besoins individuels</li>
            <li>Améliorer notre site Web</li>
            <li>Améliorer le service client et vos besoins de prise en charge</li>
            <li>Vous contacter par e-mail</li>
          </ul>
        </section>

        <section aria-labelledby="confidentialite">
          <h2 id="confidentialite" class="text-2xl font-bold mb-4 text-oxo-red">3. Confidentialité des données</h2>
          <p>
            Nous sommes les seuls propriétaires des informations recueillies sur ce site. Vos informations personnelles ne seront pas vendues, échangées, transférées, ou données à une autre société pour n’importe quelle raison, sans votre consentement.
          </p>
        </section>

        <section aria-labelledby="protection">
          <h2 id="protection" class="text-2xl font-bold mb-4 text-oxo-red">4. Protection des informations</h2>
          <p>
            Nous mettons en œuvre une variété de mesures de sécurité pour préserver la sécurité de vos informations personnelles. Nous utilisons un cryptage à la pointe de la technologie pour protéger les informations sensibles transmises en ligne.
          </p>
        </section>

        <section aria-labelledby="cookies">
          <h2 id="cookies" class="text-2xl font-bold mb-4 text-oxo-red">5. Cookies et Mesure d'Audience</h2>
          <p class="mb-4">
            Pour améliorer l'expérience utilisateur, nous utilisons <strong>Umami Analytics</strong>. Cet outil est respectueux de votre vie privée :
          </p>
          <ul class="list-disc pl-6 mb-4">
            <li>Il est hébergé par nos soins (les données restent en France).</li>
            <li>Il n'utilise <strong>pas de cookies</strong> de traçage.</li>
            <li>Toutes les données sont anonymisées.</li>
          </ul>
          <p>
            Conformément aux directives de la CNIL, cet outil est dispensé de consentement préalable. Vous pouvez toutefois choisir de désactiver tout suivi dans les paramètres de votre navigateur (option "Do Not Track").
          </p>
        </section>

        <section aria-labelledby="paiement">
          <h2 id="paiement" class="text-2xl font-bold mb-4 text-oxo-red">6. Paiement en ligne – Stripe</h2>
          <p class="mb-4">
            Pour l'inscription aux <strong>Ateliers d'Été</strong>, nous faisons appel au prestataire de paiement <strong>Stripe, Inc.</strong> (185 Berry Street, Suite 550, San Francisco, CA 94107, USA).
          </p>
          <p class="mb-4">
            Le paiement s'effectue sur une page sécurisée hébergée par Stripe. <strong>OXO Narbonne ne collecte ni ne stocke vos coordonnées bancaires.</strong>
          </p>
          <p class="mb-4">
            Lors du processus de paiement, Stripe peut déposer des <strong>cookies strictement nécessaires</strong> à la sécurisation de la transaction (lutte contre la fraude, vérification 3DS). Ces cookies ne nécessitent pas de consentement préalable conformément aux directives de la CNIL.
          </p>
          <p>
            Pour en savoir plus, consultez la <a href="https://stripe.com/fr/privacy" target="_blank" rel="noopener noreferrer" class="text-oxo-red hover:underline">politique de confidentialité de Stripe</a>.
          </p>
        </section>

        <section aria-labelledby="contact">
          <h2 id="contact" class="text-2xl font-bold mb-4 text-oxo-red">7. Contact</h2>
          <p>
            Pour toute question concernant cette politique de confidentialité, vous pouvez nous contacter à : <strong>{{ info?.email || "contact@oxo.edu" }}</strong>.
          </p>
        </section>

      </div>
    </div>
  </div>
</template>
